import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';

export type ButtonVariant = 'primary' | 'ghost' | 'danger';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual weight. `ghost` is the quiet default; `primary` is the page CTA. */
  variant?: ButtonVariant;
  /** Stretch to the full width of the container. */
  block?: boolean;
  /** Disable + mark busy while an action is in flight. */
  loading?: boolean;
  /**
   * Render the single child element instead of a <button> (Radix Slot),
   * forwarding all button styling onto e.g. an <a> or react-router <Link>.
   */
  asChild?: boolean;
}

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: 'btn-primary',
  ghost: 'btn-ghost',
  danger: 'btn-danger',
};

// Typed wrapper over the existing `.btn` classes. Emits the same markup pages
// already use, so adopting it is a no-op visually; the value is a single
// enforced API (variant/loading/asChild) instead of hand-written class strings.
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'ghost', block = false, loading = false, asChild = false, className, disabled, type, children, ...rest },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  const cls = ['btn', VARIANT_CLASS[variant], block ? 'btn-block' : '', className]
    .filter(Boolean)
    .join(' ');

  // Native <button> gets type="button" by default (avoids accidental form
  // submits) and the disabled attribute; a Slot child supplies its own.
  const nativeProps = asChild ? {} : { type: type ?? 'button', disabled: disabled || loading };

  return (
    <Comp ref={ref} className={cls} aria-busy={loading || undefined} {...nativeProps} {...rest}>
      {children}
    </Comp>
  );
});
